import { useState, useMemo } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { useSeoMeta } from "@/lib/useSeoMeta";
import { toast } from "sonner";
import { Send, CalendarClock, Loader2, CheckCircle2 } from "lucide-react";

// Génère 7 jours ouvrés à partir de demain, avec créneaux 9h→18h
function buildSlots() {
  const days = [];
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  let added = 0, offset = 1;
  while (added < 7 && offset < 20) {
    const d = new Date(now);
    d.setDate(now.getDate() + offset);
    offset++;
    const dow = d.getDay(); // 0=dim 6=sam
    if (dow === 0 || dow === 6) continue;
    days.push(d);
    added++;
  }
  const hours = ["09:00", "10:00", "11:00", "14:00", "15:00", "16:00", "17:00"];
  return { days, hours };
}

export default function ContactPage() {
  const { t, lang } = useLang();
  useSeoMeta({
    title: t("contact.seo.title") + " · EmpCo",
    description: t("contact.seo.desc"),
    canonical: `https://empco-compliance.eu/${lang === "fr" ? "" : lang + "/"}contact`,
  });
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", company: "", message: "", website: "" });
  const [slotDay, setSlotDay] = useState(null);
  const [slotHour, setSlotHour] = useState(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const { days, hours } = useMemo(() => buildSlots(), []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email || !form.phone) {
      return toast.error(t("contact.err.required"));
    }
    setSending(true);
    try {
      const preferred_slot = slotDay && slotHour
        ? `${slotDay.toISOString().slice(0, 10)}T${slotHour}`
        : null;
      await api.post("/contact", { ...form, preferred_slot, lang });
      setSent(true);
      toast.success(t("contact.success"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("contact.err.generic"));
    } finally {
      setSending(false);
    }
  };

  const dayLabel = (d) => d.toLocaleDateString(lang === "fr" ? "fr-FR" : lang, { weekday: "short", day: "2-digit", month: "short" });

  if (sent) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="contact-success">
          <CheckCircle2 className="w-16 h-16 text-primary mx-auto mb-6" />
          <h1 className="text-3xl font-serif tracking-tight mb-3">{t("contact.success.title")}</h1>
          <p className="text-muted-foreground">{t("contact.success.desc")}</p>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="max-w-3xl mx-auto px-6 py-16" data-testid="contact-page">
        <p className="eyebrow mb-1">{t("contact.eyebrow")}</p>
        <h1 className="text-4xl sm:text-5xl font-serif tracking-tight mb-4">{t("contact.title")}</h1>
        <p className="text-muted-foreground max-w-xl mb-10 leading-relaxed">{t("contact.intro")}</p>

        <form onSubmit={submit} className="space-y-8" noValidate>
          <div className="hairline p-6 rounded-lg space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <Field label={t("contact.f.first_name")} value={form.first_name}
                     onChange={(v) => setForm({ ...form, first_name: v })} required testid="contact-first-name" />
              <Field label={t("contact.f.last_name")} value={form.last_name}
                     onChange={(v) => setForm({ ...form, last_name: v })} required testid="contact-last-name" />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label={t("contact.f.email")} type="email" value={form.email}
                     onChange={(v) => setForm({ ...form, email: v })} required testid="contact-email" />
              <Field label={t("contact.f.phone")} type="tel" value={form.phone}
                     onChange={(v) => setForm({ ...form, phone: v })} required testid="contact-phone" />
            </div>
            <div>
              <Field label={t("contact.f.company")} value={form.company}
                     onChange={(v) => setForm({ ...form, company: v })} testid="contact-company" />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2 block">{t("contact.f.message")}</label>
              <textarea rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                data-testid="contact-message" maxLength={2000}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/40" />
            </div>
            {/* honeypot */}
            <input type="text" name="website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })}
              tabIndex={-1} autoComplete="off" className="absolute -left-[9999px] w-px h-px" aria-hidden="true" />
          </div>

          <div className="hairline p-6 rounded-lg" data-testid="contact-slots">
            <div className="flex items-center gap-2 mb-4">
              <CalendarClock className="w-4 h-4 text-primary" />
              <p className="eyebrow">{t("contact.slots.title")}</p>
            </div>
            <p className="text-sm text-muted-foreground mb-5">{t("contact.slots.desc")}</p>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mb-5">
              {days.map((d, i) => {
                const active = slotDay && d.toDateString() === slotDay.toDateString();
                return (
                  <button key={i} type="button" onClick={() => setSlotDay(d)}
                    data-testid={`slot-day-${i}`}
                    className={`p-3 rounded-lg border text-sm transition ${active ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary/50"}`}>
                    <p className="font-mono text-[10px] uppercase opacity-75">{dayLabel(d).split(" ")[0]}</p>
                    <p className="font-serif text-lg mt-1">{d.getDate()}</p>
                    <p className="font-mono text-[10px] opacity-75">{d.toLocaleDateString(lang === "fr" ? "fr-FR" : lang, { month: "short" })}</p>
                  </button>
                );
              })}
            </div>
            {slotDay && (
              <div>
                <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-3">{t("contact.slots.hours")}</p>
                <div className="grid grid-cols-3 sm:grid-cols-7 gap-2">
                  {hours.map((h) => (
                    <button key={h} type="button" onClick={() => setSlotHour(h)}
                      data-testid={`slot-hour-${h}`}
                      className={`p-2 rounded-lg border font-mono text-sm transition ${slotHour === h ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary/50"}`}>
                      {h}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {slotDay && slotHour && (
              <p className="mt-4 text-sm text-primary font-mono" data-testid="slot-summary">
                {t("contact.slots.chosen")} : {dayLabel(slotDay)} · {slotHour}
              </p>
            )}
          </div>

          <button type="submit" disabled={sending}
            data-testid="contact-submit"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 h-12 px-8 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-40 transition">
            {sending ? <><Loader2 className="w-4 h-4 animate-spin" /> {t("contact.sending")}</> : <><Send className="w-4 h-4" /> {t("contact.submit")}</>}
          </button>
        </form>
      </main>
      <Footer />
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testid }) {
  return (
    <div>
      <label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2 block">
        {label}{required && <span className="text-primary ml-1">*</span>}
      </label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required}
        data-testid={testid}
        className="w-full h-11 rounded-lg border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40" />
    </div>
  );
}
